const { getCookie } = require('../cookies');
class AmplitudeService {
  constructor() {
    this.apiKey = process.env.VUE_APP_AMPLITUDE_API_KEY;
    this.isLoaded = false;
    this.isReady = false;
    this.pendingCalls = [];
    this.utmParams = this.getUTMParamsFromURL();
  }

  // Initialize Amplitude tracking
  init() {
    if (this.isLoaded) return;

    // Load Amplitude script
    const script = document.createElement('script');
    script.src = process.env.VUE_APP_AMPLITUDE_URL;
    script.async = true;

    script.onload = () => {
      // Wait for amplitude to be available and fully initialized
      this.waitForAmplitude().then(() => {
        // Initialize Amplitude with session replay and autocapture
        window.amplitude.add(window.sessionReplay.plugin({ sampleRate: 1 }));
        window.amplitude.init(this.apiKey, {
          fetchRemoteConfig: true,
          serverZone: 'EU',
          autocapture: true,
          minIdLength: 0
        });

        // Wait a bit more for full initialization
        setTimeout(() => {
          this.isReady = true;
          this.setUTMProperties();
          this.processPendingCalls();
        }, 500);

        this.isLoaded = true;
      });
    };

    document.head.appendChild(script);
  }

  getUTMParamsFromURL() {
    const params = new URLSearchParams(window.location.search);
    const utmParams = {
      utm_source: params.get('utm_source') || getCookie('flash_utm_utm_source'),
      utm_medium: params.get('utm_medium') || getCookie('flash_utm_utm_medium'),
      utm_campaign: params.get('utm_campaign') || getCookie('flash_utm_utm_campaign'),
      utm_term: params.get('utm_term') || getCookie('flash_utm_utm_term'),
      utm_content: params.get('utm_content') || getCookie('flash_utm_utm_content')
    };
    return utmParams;
  }

  // Wait for Amplitude to be fully available
  waitForAmplitude() {
    return new Promise(resolve => {
      const checkAmplitude = () => {
        if (window.amplitude && window.sessionReplay) {
          resolve();
        } else {
          setTimeout(checkAmplitude, 100);
        }
      };
      checkAmplitude();
    });
  }

  // Process any pending calls that were made before SDK was ready
  processPendingCalls() {
    this.pendingCalls.forEach(call => {
      try {
        call();
      } catch (error) {
        console.error('Error processing pending Amplitude call:', error);
      }
    });
    this.pendingCalls = [];
  }

  // Queue calls if SDK not ready
  queueCall(fn) {
    if (this.isReady) {
      fn();
    } else {
      this.pendingCalls.push(fn);
    }
  }

  // Set UTM parameters as user properties
  setUTMProperties() {
    this.queueCall(() => {
      if (!window.amplitude || !window.amplitude.setUserProperties) return;

      const filteredUTMParams = Object.fromEntries(Object.entries(this.utmParams).filter(([key, value]) => value));

      if (Object.keys(filteredUTMParams).length > 0) {
        window.amplitude.setUserProperties(filteredUTMParams);
      }
    });
  }

  // Track custom event
  trackEvent(eventName, properties = {}) {
    try {
      this.queueCall(() => {
        if (!window.amplitude || !window.amplitude.track) {
          console.warn('Amplitude not ready yet');
          return;
        }

        const eventData = { ...properties, ...this.utmParams };
        console.log(`Amplitude tracking event: ${eventName}`, eventData);
        window.amplitude.track(eventName, eventData);
      });
    } catch (error) {
      console.error('Error tracking Amplitude event:', error);
    }
  }

  // Set user ID
  setUserId(userId) {
    console.log(`Setting Amplitude user ID: ${userId}`);
    this.queueCall(() => {
      if (!window.amplitude || !window.amplitude.setUserId) return;
      console.log(`Setting Amplitude user ID: ${userId}`);
      window.amplitude.setUserId(userId.toString());
    });
  }

  // Set user properties
  regenerateDeviceId;
  setUserProperties(properties) {
    this.queueCall(() => {
      if (!window.amplitude || !window.amplitude.Identify) return;
      const identify = new window.amplitude.Identify();

      Object.entries(properties).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          identify.set(key, value);
        }
      });

      console.log('Setting Amplitude user properties:', identify);
      window.amplitude.identify(identify);
    });
  }

  logout() {
    console.log('Amplitude user logout');
    this.queueCall(() => {
      if (!window.amplitude) return;

      // Reset user ID (set to null or undefined)
      if (window.amplitude.setUserId) {
        window.amplitude.setUserId(null);
      }

      // Reset device ID to generate a new anonymous session
      if (window.amplitude.regenerateDeviceId) {
        window.amplitude.regenerateDeviceId();
      }

      // Track logout event
      if (window.amplitude.track) {
        window.amplitude.track('User Logout');
      }

      // Optional: Clear user properties by setting them to null
      if (window.amplitude.Identify) {
        const identify = new window.amplitude.Identify();
        identify.clearAll(); // This clears all user properties
        window.amplitude.identify(identify);
      }

      console.log('Amplitude user logged out successfully');
    });
  }

  // Optional: Add a method to clear specific user properties
  clearUserProperties(propertyKeys = []) {
    this.queueCall(() => {
      if (!window.amplitude || !window.amplitude.Identify) return;

      const identify = new window.amplitude.Identify();

      if (propertyKeys.length === 0) {
        // Clear all properties
        identify.clearAll();
      } else {
        // Clear specific properties
        propertyKeys.forEach(key => {
          identify.unset(key);
        });
      }

      window.amplitude.identify(identify);
      console.log('Cleared Amplitude user properties:', propertyKeys);
    });
  }

  // Track page view
  trackPageView(pageName) {
    this.trackEvent('Page View', {
      page_name: pageName,
      page_url: window.location.href,
      page_path: window.location.pathname
    });
  }

  // Track form submission
  trackFormSubmission(formId, formData = {}) {
    this.trackEvent('Form Submission', {
      form_id: formId,
      ...formData
    });
  }

  // Get UTM parameters for forms
  getUTMParamsForForms() {
    return this.utmParams;
  }
}

export default new AmplitudeService();
